import { devLog } from '../../../../utils/devLog';
/**
 * VesselConfigDemo - Demonstration of Phase 3 vessel configuration conditional formatting
 * Shows how familiarisation checklists adapt based on vessel equipment
 */

import React, { useState } from 'react';
import { Ship, Settings, Check, X } from 'lucide-react';
import { useVessel } from 'contexts/VesselContext';
import { generateFamiliarisationWithCustomItems } from '../../../../utils/familiarisationHelpers';

const VesselConfigDemo = () => {
  const { currentVesselId, getCurrentVessel, setCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const [demoView, setDemoView] = useState('comparison');
  
  // Sample crew member for demo
  const sampleCrew = {
    id: 'demo-1',
    name: 'John Demo',
    role: 'Deckhand',
    vesselType: 'ctv'
  };

  // Two vessel configurations for comparison
  const basicVessel = {
    vesselName: 'MV Basic',
    vesselType: 'ctv',
    category: '2',
    lengthOverall: '20',
    hasLiferaft: false,
    hasEPIRB: false,
    hasSART: false,
    hasAccommodation: false,
    hasGalley: false,
    hasEnclosedSpaces: false,
    hasEmergencySteering: false
  };

  const fullVessel = {
    vesselName: 'MV Advanced',
    vesselType: 'ctv',
    category: '0',
    lengthOverall: '35',
    hasLiferaft: true,
    hasEPIRB: true,
    hasSART: true,
    hasAccommodation: true,
    hasGalley: true,
    hasEnclosedSpaces: true,
    hasEmergencySteering: true,
    internationalVoyages: true,
    oilCapacityLitres: '500'
  };

  // Generate familiarisation for both vessels
  const basicFamiliarisation = generateFamiliarisationWithCustomItems(basicVessel, sampleCrew, []);
  const fullFamiliarisation = generateFamiliarisationWithCustomItems(fullVessel, sampleCrew, []);

  // Count items for emergency equipment section (index 1)
  const emergencySection = basicFamiliarisation.sections[1];
  const emergencySectionFull = fullFamiliarisation.sections[1];

  return (
    <div className="space-y-6">
      {/* Demo Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Ship className="h-6 w-6 text-cyan-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Vessel Configuration Conditional Formatting Demo
            </h2>
          </div>
          <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
            Phase 3 Complete
          </span>
        </div>
        <p className="text-gray-600">
          This demonstrates how familiarisation checklists automatically adapt based on vessel equipment configuration.
          Items that don't apply to your vessel are automatically hidden.
        </p>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => setDemoView('comparison')}
          className={`px-4 py-2 rounded-md ${
            demoView === 'comparison' 
              ? 'bg-cyan-600 text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Side-by-Side Comparison
        </button>
        <button
          onClick={() => setDemoView('current')}
          className={`px-4 py-2 rounded-md ${
            demoView === 'current' 
              ? 'bg-cyan-600 text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Current Vessel
        </button>
      </div>

      {/* Comparison View */}
      {demoView === 'comparison' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Vessel */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Basic Vessel Configuration</h3>
              <p className="text-sm text-gray-600 mt-1">Small CTV with minimal equipment</p>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <X className="h-4 w-4 text-red-500" />
                  <span>No Liferaft</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="h-4 w-4 text-red-500" />
                  <span>No EPIRB</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="h-4 w-4 text-red-500" />
                  <span>No SART</span>
                </div>
                <div className="flex items-center gap-2">
                  <X className="h-4 w-4 text-red-500" />
                  <span>No Emergency Steering</span>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-gray-50 rounded">
                <p className="text-sm font-medium text-gray-900">
                  Emergency Equipment Items: {emergencySection.items.length}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {emergencySection.filteredItemsCount} items automatically hidden
                </p>
              </div>

              <div className="mt-4">
                <p className="text-xs font-medium text-gray-700 mb-2">Sample Hidden Items:</p>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li>• Life raft deployment procedures</li>
                  <li>• EPIRB activation procedures</li>
                  <li>• SART location and use</li>
                  <li>• Emergency steering operation</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Full Vessel */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Advanced Vessel Configuration</h3>
              <p className="text-sm text-gray-600 mt-1">Large CTV with full equipment suite</p>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Liferaft Fitted</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>EPIRB Fitted</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>SART Fitted</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Emergency Steering</span>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-green-50 rounded">
                <p className="text-sm font-medium text-gray-900">
                  Emergency Equipment Items: {emergencySectionFull.items.length}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  All equipment-specific items included
                </p>
              </div>

              <div className="mt-4">
                <p className="text-xs font-medium text-gray-700 mb-2">Additional Items Shown:</p>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li>✓ Life raft deployment procedures</li>
                  <li>✓ EPIRB activation procedures</li>
                  <li>✓ SART location and use</li>
                  <li>✓ Emergency steering operation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Current Vessel View */}
      {demoView === 'current' && currentVessel && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">
                  {currentVessel.name || 'Current Vessel'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {currentVessel.type} • Category {currentVessel.category}
                </p>
              </div>
              <button
                onClick={() => {
                  // This is a demo - we'll just simulate the vessel switching
                  // In reality, you'd update the vessel configuration through proper channels
                  devLog('Demo: switching vessel configuration');
                }}
                className="flex items-center gap-2 px-3 py-1 bg-cyan-100 text-cyan-700 rounded-md hover:bg-cyan-200"
              >
                <Settings className="h-4 w-4" />
                Toggle Config
              </button>
            </div>
          </div>
          
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-3 bg-gray-50 rounded">
                <p className="text-2xl font-bold text-gray-900">
                  {currentVessel.hasLiferaft ? '✓' : '✗'}
                </p>
                <p className="text-xs text-gray-600">Liferaft</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded">
                <p className="text-2xl font-bold text-gray-900">
                  {currentVessel.hasEPIRB ? '✓' : '✗'}
                </p>
                <p className="text-xs text-gray-600">EPIRB</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded">
                <p className="text-2xl font-bold text-gray-900">
                  {currentVessel.hasSART ? '✓' : '✗'}
                </p>
                <p className="text-xs text-gray-600">SART</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded">
                <p className="text-2xl font-bold text-gray-900">
                  {currentVessel.hasEmergencySteering ? '✓' : '✗'}
                </p>
                <p className="text-xs text-gray-600">Emergency Steering</p>
              </div>
            </div>

            <div className="bg-cyan-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">How it works:</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-600 mt-0.5">•</span>
                  <span>Checklist items automatically show/hide based on vessel equipment</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-600 mt-0.5">•</span>
                  <span>Crew only see relevant training items for their vessel</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-600 mt-0.5">•</span>
                  <span>Reduces confusion and focuses on applicable requirements</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-600 mt-0.5">•</span>
                  <span>Maintains compliance with WBC3 requirements</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Implementation Notes */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Implementation Complete ✅</h4>
        <ul className="space-y-1 text-sm text-blue-800">
          <li>• Vessel configuration stored in app context</li>
          <li>• Familiarisation checklists filter based on equipment presence</li>
          <li>• Visual indicators show when items are hidden</li>
          <li>• Works with both regulatory and custom company items</li>
        </ul>
      </div>
    </div>
  );
};

export default VesselConfigDemo;